import React from 'react';
import PropTypes from 'prop-types';
import URL from 'urijs';
import _ from 'underscore';
import classnames from 'classnames';
import { CustomerTile } from './customer-tile';

const renderCustomersList = (
  customers,
  customerUrlUpdater,
  url,
  classes,
  isCustomerHierarchyEnabled,
  highlightCurrentCustomer
) => (
  customers.map((customer) => {
    const currentCustomerCss = highlightCurrentCustomer && customer.IsCurrent ? classes.currentCustomerListItem : '';
    const customerUrl = customerUrlUpdater({ url, cid: customer.Id, uid: customer.UserId });
    const customerNumber = isCustomerHierarchyEnabled ? customer.Number : customer.Id;
    return (
      <li className={classnames('customer-selector-list-item', currentCustomerCss, classes.customerListItem)} key={customer.Id}>
        <CustomerTile
          customerUrl={customerUrl}
          displayName={customer.DisplayName}
          displayAccountCount={customer.DisplayAccountCount}
          customerNumber={customerNumber}
          classes={classes}
        />
      </li>
    );
  })
);

export const CustomerList = ({
  customers,
  searchText,
  i18n,
  viewAllCustomersUrl,
  maxNumberofRecentCustomerShown,
  url,
  customerUrlUpdater,
  classes,
  isCustomerHierarchyEnabled,
  renderLink: RenderLink,
  isViewAllCustomerShowAsFallback,
}) => {
  // if this is a search text to match,
  // filter the passed in customers array and render a flatten list
  if (searchText) {
    const customersInSearchResult = customers.filter(customer =>
      customer.Name.toLowerCase().indexOf(searchText) > -1 ||
      customer.Id.toString().toLowerCase().indexOf(searchText) > -1 ||
      customer.Number.toString().toLowerCase().indexOf(searchText) > -1);
    return (
      <ul className={classnames('customer-selector-search-customer-list', classes.customerList)}>
        {renderCustomersList(
          customersInSearchResult, customerUrlUpdater, url,
          classes, isCustomerHierarchyEnabled
        )}
      </ul>
    );
  }
  // move default customer to the head of the list
  const defaultCustomerIndex = _.findIndex(customers, customer => customer.IsDefault);
  let fullCustomerList = customers;
  if (defaultCustomerIndex !== -1) {
    fullCustomerList = [
      customers[defaultCustomerIndex],
      ...customers.slice(0, defaultCustomerIndex),
      ...customers.slice(defaultCustomerIndex + 1),
    ];
  }

  // construct recent customer list
  const recentCustomerList = customers.filter(customer => customer.LastAccessedUtc)
    .sort((customer1, customer2) => customer1.LastAccessedUtc - customer2.LastAccessedUtc)
    .slice(0, maxNumberofRecentCustomerShown);

  return (
    <React.Fragment>
      {recentCustomerList.length > 0 &&
        <React.Fragment>
          <div role="group" aria-labelledby="recent-title">
            <div className={classnames('customer-selector-recent-header', classes.recentCustomersHeader)}>
              <span className={classnames('title-span', classes.customerListHeaderTitle)} id="recent-title">{i18n.getString(_TL_('Recent'))}</span>
            </div>
            <ul className={classnames('customer-selector-recent-customer-list', classes.customerList)}>
              {renderCustomersList(
                recentCustomerList, customerUrlUpdater,
                url, classes, isCustomerHierarchyEnabled
              )}
            </ul>
          </div>
        </React.Fragment>
      }
      <div role="group" aria-labelledby="manager-accounts-title">
        <div className={classnames('customer-selector-manage-header', classes.manageCustomersHeader)}>
          <span className={classnames('title-span', classes.customerListHeaderTitle)} id="manager-accounts-title">{i18n.getString(_TL_('Manager accounts'))}</span>
          <RenderLink
            href={new URL(viewAllCustomersUrl).setQuery('rurl', url).toString()}
            className="customer-selector__view-customers"
            ariaLabel={i18n.getString(_TL_('View all Manager accounts'))}
            isFallback={isViewAllCustomerShowAsFallback}
            placement="left"
          >
            {i18n.getString(_TL_('View all'))}
          </RenderLink>
        </div>
        <ul className={classnames('customer-selector-manage-customer-list', classes.customerList)}>
          {renderCustomersList(
            fullCustomerList, customerUrlUpdater,
            url, classes, isCustomerHierarchyEnabled, true
          )}
        </ul>
      </div>
    </React.Fragment>
  );
};

CustomerList.propTypes = {
  customers: PropTypes.arrayOf(PropTypes.object).isRequired,
  searchText: PropTypes.string.isRequired,
  i18n: PropTypes.shape({ getString: PropTypes.func }).isRequired,
  viewAllCustomersUrl: PropTypes.string.isRequired,
  maxNumberofRecentCustomerShown: PropTypes.number.isRequired,
  url: PropTypes.string.isRequired,
  customerUrlUpdater: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    customerList: PropTypes.string,
    customerListItem: PropTypes.string,
    manageCustomersHeader: PropTypes.string,
    customerListHeaderTitle: PropTypes.string,
    currentCustomerListItem: PropTypes.string,
    recentCustomersHeader: PropTypes.string,
  }).isRequired,
  isCustomerHierarchyEnabled: PropTypes.bool.isRequired,
  renderLink: PropTypes.func.isRequired,
  isViewAllCustomerShowAsFallback: PropTypes.bool.isRequired,
};
